<template>
  <!-- 頁面標題 -->
  <div class="page-header">
    <h1>訂單管理</h1>
    <div class="header-actions">
      <button class="btn-export" @click="exportReport">匯出訂單數據</button>
    </div>
  </div>

  <!-- 搜尋與篩選 -->
  <div class="search-filter">
    <div class="search-box">
      <input
        type="text"
        v-model="searchQuery"
        placeholder="搜尋訂單編號、客戶名稱或聯絡人..."
        @input="handleSearch"
      />
    </div>

    <div class="filter-dropdown">
      <select v-model="activeFilter" class="form-input">
        <option
          v-for="filter in filters"
          :key="filter.value"
          :value="filter.value"
        >
          {{ filter.label }} ({{ filter.count ?? 0 }})
        </option>
      </select>
    </div>
  </div>

  <!-- 手機版 - 訂單卡片列表 -->
  <div class="order-list-mobile">
    <div class="order-card" v-for="order in paginatedOrders" :key="order.id">
      <!-- 訂單標題列 -->
      <div class="order-header">
        <div class="order-title-section">
          <h3 class="order-number">{{ order.orderNumber }}</h3>
          <div class="badges-group">
            <span class="order-badge" :class="getStatusClass(order.status)">
              {{ getStatusText(order.status) }}
            </span>
            <span
              class="payment-badge"
              :class="getPaymentClass(order.paymentStatus)"
            >
              {{ getPaymentText(order.paymentStatus) }}
            </span>
          </div>
        </div>
        <div class="order-actions-top">
          <button
            class="btn-icon-small"
            @click="viewOrderDetail(order)"
            title="查看詳情"
          >
            <img src="@/assets/image/icon/search.png" class="small-all-icon" />
          </button>
          <div class="dropdown-wrapper">
            <button
              class="btn-icon-small"
              @click.stop="togglePaymentDropdown(order.id)"
              title="調整付款狀態"
            >
              <img src="@/assets/image/icon/card.png" class="small-all-icon" />
            </button>
            <div
              class="dropdown-menu payment-dropdown"
              v-if="activePaymentDropdown === order.id"
              @click.stop
            >
              <button
                @click="
                  updatePaymentStatus(order, 'paid');
                  activePaymentDropdown = null;
                "
                class="dropdown-item"
                :class="{ active: order.paymentStatus === 'paid' }"
              >
                已付款
              </button>
              <button
                @click="
                  updatePaymentStatus(order, 'unpaid');
                  activePaymentDropdown = null;
                "
                class="dropdown-item"
                :class="{ active: order.paymentStatus === 'unpaid' }"
              >
                未付款
              </button>
            </div>
          </div>

          <!-- ⭐ 新增:出貨狀態按鈕 -->
          <div class="dropdown-wrapper">
            <button
              class="btn-icon-small"
              @click.stop="toggleStatusDropdown(order.id)"
              title="調整出貨狀態"
            >
              <img src="@/assets/image/icon/truck.png" class="small-all-icon" />
            </button>
            <div
              class="dropdown-menu status-dropdown"
              v-if="activeStatusDropdown === order.id"
              @click.stop
            >
              <button
                @click="
                  updateOrderStatus(order, '待處理');
                  activeStatusDropdown = null;
                "
                class="dropdown-item"
                :class="{ active: order.status === '待處理' }"
              >
                待處理
              </button>

              <button
                @click="
                  updateOrderStatus(order, '處理中');
                  activeStatusDropdown = null;
                "
                class="dropdown-item"
                :class="{ active: order.status === '處理中' }"
              >
                處理中
              </button>

              <button
                @click="
                  updateOrderStatus(order, '已出貨');
                  activeStatusDropdown = null;
                "
                class="dropdown-item"
                :class="{ active: order.status === '已出貨' }"
              >
                已出貨
              </button>

              <button
                @click="
                  updateOrderStatus(order, '已完成');
                  activeStatusDropdown = null;
                "
                class="dropdown-item"
                :class="{ active: order.status === '已完成' }"
              >
                已完成
              </button>

              <button
                @click="
                  updateOrderStatus(order, '已取消');
                  activeStatusDropdown = null;
                "
                class="dropdown-item"
                :class="{ active: order.status === '已取消' }"
              >
                已取消
              </button>

              <button
                @click="
                  updateOrderStatus(order, '處理中');
                  activeStatusDropdown = null;
                "
                class="dropdown-item"
                :class="{ active: order.status === 'processing' }"
              >
                處理中
              </button>
              <button
                @click="
                  updateOrderStatus(order, '已出貨');
                  activeStatusDropdown = null;
                "
                class="dropdown-item"
                :class="{ active: order.status === 'shipped' }"
              >
                已出貨
              </button>
              <button
                @click="
                  updateOrderStatus(order, '已完成');
                  activeStatusDropdown = null;
                "
                class="dropdown-item"
                :class="{ active: order.status === 'completed' }"
              >
                已完成
              </button>
              <button
                @click="
                  updateOrderStatus(order, 'cancelled');
                  activeStatusDropdown = null;
                "
                class="dropdown-item"
                :class="{ active: order.status === 'cancelled' }"
              >
                已取消
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- 客戶資訊 -->
      <div class="order-customer">
        <div class="customer-info-row">
          <span class="info-label">客戶</span>
          <span class="info-value">{{ order.customerName }}</span>
        </div>
        <div class="customer-info-row">
          <span class="info-label">聯絡人</span>
          <span class="info-value">{{ order.contactPerson }}</span>
        </div>
        <div class="customer-info-row">
          <span class="info-label">電話</span>
          <span class="info-value">{{ order.phone }}</span>
        </div>
      </div>

      <!-- 訂單資訊 -->
      <div class="order-info">
        <div class="info-row">
          <span class="info-label"> 商品項目</span>
          <span class="info-value">{{ order.productCount }} 項</span>
        </div>
        <div class="info-row">
          <span class="info-label">訂單日期</span>
          <span class="info-value">{{ order.orderDate }}</span>
        </div>
        <div class="info-row" v-if="order.shippedDate">
          <span class="info-label">出貨日期</span>
          <span class="info-value">{{ order.shippedDate }}</span>
        </div>
        <div class="info-row total-row">
          <span class="info-label">總金額</span>
          <span class="info-value total-amount"
            >NT$ {{ formatNumber(order.totalAmount) }}</span
          >
        </div>
      </div>

      <!-- 手機版操作按鈕區域 -->
      <div class="order-card-actions-mobile">
        <button class="btn-mobile-action view" @click="viewOrderDetail(order)">
          查看詳情
        </button>

        <div class="grid-item dropdown-wrapper-mobile">
          <button
            class="btn-mobile-action pay"
            @click.stop="togglePaymentDropdown(order.id)"
          >
            付款狀態
          </button>
          <div
            class="dropdown-menu payment-dropdown"
            v-if="activePaymentDropdown === order.id"
            @click.stop
          >
            <button
              @click="
                updatePaymentStatus(order, 'paid');
                activePaymentDropdown = null;
              "
              class="dropdown-item"
              :class="{ active: order.paymentStatus === 'paid' }"
            >
              已付款
            </button>
            <button
              @click="
                updatePaymentStatus(order, 'unpaid');
                activePaymentDropdown = null;
              "
              class="dropdown-item"
              :class="{ active: order.paymentStatus === 'unpaid' }"
            >
              未付款
            </button>
          </div>
        </div>

        <div class="grid-item dropdown-wrapper-mobile">
          <button
            class="btn-mobile-action shop"
            @click.stop="toggleStatusDropdown(order.id)"
          >
            出貨狀態
          </button>
          <div
            class="dropdown-menu status-dropdown"
            v-if="activeStatusDropdown === order.id"
            @click.stop
          >
            <button
              @click="
                updateOrderStatus(order, '待處理');
                activeStatusDropdown = null;
              "
              class="dropdown-item"
              :class="{ active: order.status === '待處理' }"
            >
              待處理
            </button>
            <button
              @click="
                updateOrderStatus(order, '處理中');
                activeStatusDropdown = null;
              "
              class="dropdown-item"
              :class="{ active: order.status === '處理中' }"
            >
              處理中
            </button>
            <button
              @click="
                updateOrderStatus(order, '已出貨');
                activeStatusDropdown = null;
              "
              class="dropdown-item"
              :class="{ active: order.status === '已出貨' }"
            >
              已出貨
            </button>
            <button
              @click="
                updateOrderStatus(order, '已完成');
                activeStatusDropdown = null;
              "
              class="dropdown-item"
              :class="{ active: order.status === '已完成' }"
            >
              已完成
            </button>
            <button
              @click="
                updateOrderStatus(order, '已取消');
                activeStatusDropdown = null;
              "
              class="dropdown-item"
              :class="{ active: order.status === '已取消' }"
            >
              已取消
            </button>
          </div>
        </div>
      </div>

      <!-- 訂單操作按鈕 -->
      <div class="order-actions">
        <button
          v-if="order.status === 'pending'"
          class="btn-action btn-process"
          @click="updateOrderStatus(order, 'processing')"
        >
          ✓ 開始處理
        </button>
        <button
          v-if="order.status === 'processing'"
          class="btn-action btn-ship"
          @click="updateOrderStatus(order, 'shipped')"
        >
          🚚 標記已出貨
        </button>
        <button
          v-if="order.status === 'shipped'"
          class="btn-action btn-complete"
          @click="updateOrderStatus(order, 'completed')"
        >
          ✅ 完成訂單
        </button>
      </div>
    </div>
  </div>

  <!-- 電腦版 - 訂單表格 -->
  <div class="order-table-desktop">
    <div class="table-container">
      <table class="order-table">
        <thead>
          <tr>
            <th>訂單編號</th>
            <th>客戶名稱</th>
            <th>聯絡人</th>
            <th>商品項目</th>
            <th>訂單金額</th>
            <th>訂單日期</th>
            <th>付款方式</th>
            <th>訂單狀態</th>
            <th>付款狀態</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="order in paginatedOrders"
            :key="order.id"
            class="table-row"
          >
            <td class="order-number-cell">
              <span
                class="order-number-text clickable"
                @click="viewOrderDetail(order)"
              >
                {{ order.orderNumber }}
              </span>
            </td>
            <td>
              <div class="customer-cell">
                <div class="customer-name">{{ order.customerName }}</div>
                <div class="customer-phone">{{ order.phone }}</div>
              </div>
            </td>
            <td>{{ order.contactPerson }}</td>
            <td class="items-cell">
              <button
                class="items-btn"
                @click="viewOrderDetail(order)"
                :title="getItemsPreview(order)"
              >
                {{ order.productCount }} 項商品
              </button>
            </td>
            <td class="amount-cell">
              <span class="amount-text"
                >NT$ {{ formatNumber(order.totalAmount) }}</span
              >
            </td>
            <td>
              <div class="date-cell">
                <div class="date-main">{{ order.orderDate }}</div>
              </div>
            </td>
            <td>
              <span
                :class="[
                  'payment-method-badge',
                  getPaymentMethodClass(order.paymentMethod),
                ]"
              >
                {{ order.paymentMethod || "未提供" }}
              </span>
            </td>

            <td>
              <span class="order-badge" :class="getStatusClass(order.status)">
                {{ getStatusText(order.status) }}
              </span>
            </td>
            <td>
              <span
                class="payment-badge"
                :class="getPaymentClass(order.paymentStatus)"
              >
                {{ getPaymentText(order.paymentStatus) }}
              </span>
            </td>
            <td class="actions-cell">
              <div class="table-actions">
                <button
                  class="btn-table-action"
                  @click="viewOrderDetail(order)"
                  title="查看詳情"
                >
                  <img
                    src="@/assets/image/icon/search.png"
                    class="small-all-icon"
                  />
                </button>
                <!-- ⭐ 新增：付款狀態按鈕 -->
                <div class="dropdown-wrapper">
                  <button
                    class="btn-table-action"
                    @click.stop="togglePaymentDropdown(order.id)"
                    title="調整付款狀態"
                  >
                    <img
                      src="@/assets/image/icon/card.png"
                      class="small-all-icon"
                    />
                  </button>
                  <div
                    class="dropdown-menu payment-dropdown"
                    v-if="activePaymentDropdown === order.id"
                    @click.stop
                  >
                    <button
                      @click="
                        updatePaymentStatus(order, 'paid');
                        activePaymentDropdown = null;
                      "
                      class="dropdown-item"
                      :class="{ active: order.paymentStatus === 'paid' }"
                    >
                      已付款
                    </button>
                    <button
                      @click="
                        updatePaymentStatus(order, 'unpaid');
                        activePaymentDropdown = null;
                      "
                      class="dropdown-item"
                      :class="{ active: order.paymentStatus === 'unpaid' }"
                    >
                      未付款
                    </button>
                  </div>
                </div>

                <!-- ⭐ 新增：出貨狀態按鈕 -->
                <div class="dropdown-wrapper">
                  <button
                    class="btn-table-action"
                    @click.stop="toggleStatusDropdown(order.id)"
                    title="調整出貨狀態"
                  >
                    <img
                      src="@/assets/image/icon/truck.png"
                      class="small-all-icon"
                    />
                  </button>
                  <div
                    class="dropdown-menu status-dropdown"
                    v-if="activeStatusDropdown === order.id"
                    @click.stop
                  >
                    <button
                      @click="
                        updateOrderStatus(order, '待處理');
                        activeStatusDropdown = null;
                      "
                      class="dropdown-item"
                      :class="{ active: order.status === '待處理' }"
                    >
                      待處理
                    </button>

                    <button
                      @click="
                        updateOrderStatus(order, '處理中');
                        activeStatusDropdown = null;
                      "
                      class="dropdown-item"
                      :class="{ active: order.status === '處理中' }"
                    >
                      處理中
                    </button>

                    <button
                      @click="
                        updateOrderStatus(order, '已出貨');
                        activeStatusDropdown = null;
                      "
                      class="dropdown-item"
                      :class="{ active: order.status === '已出貨' }"
                    >
                      已出貨
                    </button>

                    <button
                      @click="
                        updateOrderStatus(order, '已完成');
                        activeStatusDropdown = null;
                      "
                      class="dropdown-item"
                      :class="{ active: order.status === '已完成' }"
                    >
                      已完成
                    </button>

                    <button
                      @click="
                        updateOrderStatus(order, '已取消');
                        activeStatusDropdown = null;
                      "
                      class="dropdown-item"
                      :class="{ active: order.status === '已取消' }"
                    >
                      已取消
                    </button>
                  </div>
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>

  <!-- 查看訂單詳情 Modal -->
  <div class="modal" v-if="showDetailModal">
    <div class="modal-content large">
      <div class="modal-header">
        <h2>訂單詳情 - {{ selectedOrder?.orderNumber }}</h2>
        <button class="close-btn" @click="showDetailModal = false">×</button>
      </div>

      <div class="modal-body">
        <div class="detail-section">
          <h3 class="section-title">基本資訊</h3>
          <div class="detail-grid">
            <div class="detail-item">
              <span class="detail-label">訂單編號</span>
              <span class="detail-value">{{ selectedOrder?.orderNumber }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">訂單狀態</span>
              <span class="detail-value">
                <span
                  class="order-badge"
                  :class="getStatusClass(selectedOrder?.status)"
                >
                  {{ getStatusText(selectedOrder?.status) }}
                </span>
              </span>
            </div>
            <div class="detail-item">
              <span class="detail-label">付款狀態</span>
              <span class="detail-value">
                <span
                  class="payment-badge"
                  :class="getPaymentClass(selectedOrder?.paymentStatus)"
                >
                  {{ getPaymentText(selectedOrder?.paymentStatus) }}
                </span>
              </span>
            </div>
            <div class="detail-item">
              <span class="detail-label">訂單日期</span>
              <span class="detail-value">{{ selectedOrder?.orderDate }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">出貨日期</span>
              <span class="detail-value">{{
                selectedOrder?.shippedDate || "未設定"
              }}</span>
            </div>
          </div>
        </div>

        <div class="detail-section">
          <h3 class="section-title">客戶資訊</h3>
          <div class="detail-grid">
            <div class="detail-item">
              <span class="detail-label">客戶名稱</span>
              <span class="detail-value">{{
                selectedOrder?.customerName
              }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">聯絡人</span>
              <span class="detail-value">{{
                selectedOrder?.contactPerson
              }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">聯絡電話</span>
              <span class="detail-value">{{ selectedOrder?.phone }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">送貨地址</span>
              <span class="detail-value">{{ selectedOrder?.address }}</span>
            </div>
          </div>
        </div>

        <div class="detail-section">
          <h3 class="section-title">商品明細</h3>
          <div class="detail-table">
            <table>
              <thead>
                <tr>
                  <th>商品名稱</th>
                  <th>單價</th>
                  <th>數量</th>
                  <th>小計</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in selectedOrder?.items" :key="item.id">
                  <td>{{ item.name }}</td>
                  <td>NT$ {{ formatNumber(item.price) }}</td>
                  <td>{{ item.quantity }}</td>
                  <td>NT$ {{ formatNumber(item.price * item.quantity) }}</td>
                </tr>
              </tbody>
              <tfoot>
                <tr>
                  <td colspan="3" class="text-right">
                    <strong>總計</strong>
                  </td>
                  <td>
                    <strong
                      >NT$
                      {{ formatNumber(selectedOrder?.totalAmount) }}</strong
                    >
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        <div class="detail-section" v-if="selectedOrder?.note">
          <h3 class="section-title">備註</h3>
          <div class="note-content">
            {{ selectedOrder?.note }}
          </div>
        </div>
      </div>

      <div class="modal-footer">
        <button class="btn-cancel" @click="showDetailModal = false">
          關閉
        </button>
        <button class="btn-submit" @click="generatePDF(selectedOrder)">
          下載出貨單
        </button>
      </div>
    </div>
  </div>
  <!-- 分頁控制區 -->
  <div class="pagination">
    <button
      class="btn btn-sm"
      :disabled="currentPage === 1"
      @click="currentPage--"
    >
      ◀ 上一頁
    </button>

    <span class="page-info"> 第 {{ currentPage }} / {{ totalPages }} 頁 </span>

    <button
      class="btn btn-sm"
      :disabled="currentPage === totalPages"
      @click="currentPage++"
    >
      下一頁 ▶
    </button>
  </div>
</template>

<script src="@/assets/js/order.js"></script>
<style scoped src="@/assets/css/order.css"></style>
